// Chatbot Service - Contains all chatbot-related API calls
import { CHATBOT_CONFIG, logChatbotConfig, getCountryInfo } from './chatbotConfig';

export interface Message { 
    sender: string; 
    text: string; 
    type?: string; 
    exchange_id?: string;
    id?: string | number; // Database ID (number from API, string for temp messages)
    uiId?: string; // UI-specific ID for animations
    feedback?: number | null; // 1 for thumbs up, 0 for thumbs down, null for no feedback - mapped from API's feedback_flag
    ai_agent_response_time?: number | null; // Response time in milliseconds for AI responses
}

export interface Chat { 
    id: number; 
    title: string;
    [key: string]: any; 
}

export interface CreateChatRequest {
    userId: number;
    title: string;
}

export interface AddMessageRequest {
    chatId: number;
    sender: string;
    text: string;
    exchange_id?: string;
    ai_agent_response_time?: number | null; // Add response time for bot messages
}

export interface SendMessageRequest {
    message: string;
    exchange_id?: string;
}

export interface FeedbackRequest {
    messageId: number;
    feedbackFlag: number; // 1 for thumbs up, 0 for thumbs down
}

export interface FeedbackMessageRequest {
    messageId: number;
    feedbackMessage: string; // User's detailed feedback message
}

export interface ApiResponse {
    response?: string;  // API returns "response" not "reply"
    reply?: string;     // Keep for backward compatibility
    exchange_id?: string;
    response_time?: number; // Add response time tracking
    [key: string]: any;
}

class ChatbotService {
    private baseUrl: string;
    private apiKey: string;
    private agentId: string;
    private databases: readonly {id: string, name: string}[];
    private interactUrl: string;
    
    // Performance caching
    private chatCache = new Map<number, Chat[]>();
    private messageCache = new Map<number, Message[]>();
    private cacheTimeout = 2 * 60 * 1000; // 2 minutes - shorter cache for more fresh data
    private cacheTimestamps = new Map<string, number>();
    
    // Request abortion for performance
    private abortControllers = new Map<string, AbortController>();

    constructor() {
        this.baseUrl = CHATBOT_CONFIG.BASE_URL;
        this.apiKey = CHATBOT_CONFIG.API_KEY;
        this.agentId = CHATBOT_CONFIG.AGENT_ID;
        this.databases = CHATBOT_CONFIG.DATABASES;
        this.interactUrl = CHATBOT_CONFIG.INTERACT_URL;
        
        // Log country-specific configuration on initialization (only in development)
        if (process.env.NODE_ENV === 'development') {
            console.log('🚀 ChatbotService initialized for country:', getCountryInfo());
        }
        logChatbotConfig();
    }

    private isCacheValid(key: string): boolean {
        const timestamp = this.cacheTimestamps.get(key);
        return timestamp ? (Date.now() - timestamp < this.cacheTimeout) : false;
    }

    private setCacheTimestamp(key: string): void {
        this.cacheTimestamps.set(key, Date.now());
    }

    private createAbortController(key: string): AbortController {
        // Only abort existing request if it's a different chat ID for the same operation
        const existing = this.abortControllers.get(key);
        if (existing && !existing.signal.aborted) {
            // Don't abort the same request, only different chats for same operation
            const currentChatMatch = key.match(/fetchMessages_(\d+)/);
            if (currentChatMatch) {
                // Only abort if there's a different chat being loaded
                const otherKeys = Array.from(this.abortControllers.keys())
                    .filter(k => k.startsWith('fetchMessages_') && k !== key);
                otherKeys.forEach(otherKey => {
                    const otherController = this.abortControllers.get(otherKey);
                    if (otherController && !otherController.signal.aborted) {
                        otherController.abort();
                        this.abortControllers.delete(otherKey);
                    }
                });
            }
            // Don't abort the current request
        }
        
        // Remove any existing controller for this exact key
        this.abortControllers.delete(key);
        
        const controller = new AbortController();
        this.abortControllers.set(key, controller);
        return controller;
    }

    private removeAbortController(key: string): void {
        this.abortControllers.delete(key);
    }

    /**
     * Clear cache for better performance when data is updated fix
     */
    clearUserCache(userId: number): void {
        this.chatCache.delete(userId);
        this.cacheTimestamps.delete(`chats_${userId}`);
    }

    clearChatCache(chatId: number): void {
        this.messageCache.delete(chatId);
        this.cacheTimestamps.delete(`messages_${chatId}`);
    }

    clearAllCache(): void {
        this.chatCache.clear();
        this.messageCache.clear();
        this.cacheTimestamps.clear();
    }

    /**
     * Fetch all chats for a specific user with caching
     */
    async fetchChats(userId: number): Promise<Chat[]> {
        const cacheKey = `chats_${userId}`;
        
        // Return cached data if valid
        if (this.chatCache.has(userId) && this.isCacheValid(cacheKey)) {
            return this.chatCache.get(userId)!;
        }

        // Create an abort controller for this request
        const controller = this.createAbortController(`fetchChats_${userId}`);
        
        try {
            const response = await fetch(`${this.baseUrl}/chats/${userId}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json'
                },
                signal: controller.signal // Attach the abort signal
            });

            if (!response.ok) {
                throw new Error(`Failed to fetch chats: ${response.statusText}`);
            }

            const chats = await response.json();

            // Update cache
            this.chatCache.set(userId, chats);
            this.setCacheTimestamp(cacheKey);

            return chats;
        } catch (error) {
            console.error('Error fetching chats:', error);
            throw new Error('Error fetching chats');
        } finally {
            // Remove the abort controller after the request is complete
            this.removeAbortController(`fetchChats_${userId}`);
        }
    }

    /**
     * Fetch all messages for a specific chat with caching and feedback information
     */
    async fetchMessages(chatId: number): Promise<Message[]> {
        const cacheKey = `messages_${chatId}`;
        
        // Return cached data if valid
        if (this.messageCache.has(chatId) && this.isCacheValid(cacheKey)) {
            return this.messageCache.get(chatId)!;
        }

        // Use chat-specific key instead of timestamp-based key to avoid unnecessary cancellations
        const requestKey = `fetchMessages_${chatId}`;
        const controller = this.createAbortController(requestKey);
        
        try {
            const url = `${this.baseUrl}/chats/${chatId}/messages`;
            
            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json'
                },
                signal: controller.signal // Attach the abort signal
            });

            if (!response.ok) {
                const errorText = await response.text();
                console.error(`❌ API Error Details:`, {
                    status: response.status,
                    statusText: response.statusText,
                    url: url,
                    errorBody: errorText,
                    headers: Object.fromEntries(response.headers.entries())
                });
                throw new Error(`Failed to fetch messages: ${response.status} ${response.statusText} - ${errorText}`);
            }

            const messages = await response.json();
            
            // Normalize feedback field and response time - handle different field names from API
            const normalizedMessages = messages.map((msg: any) => {
                // Map feedback_flag from API to feedback for frontend
                const feedback = msg.feedback_flag;
                
                return {
                    ...msg,
                    feedback: feedback, // Use feedback_flag value as feedback
                    ai_agent_response_time: msg.ai_agent_response_time || msg.AI_Agent_Response_Time || null // Handle different case variations
                };
            });
            
            // Update cache
            this.messageCache.set(chatId, normalizedMessages);
            this.setCacheTimestamp(cacheKey);

            return normalizedMessages;
        } catch (error) {
            // Check if it's an abort error (request was cancelled)
            if ((error as any)?.name === 'AbortError') {
                // Don't throw error for cancelled requests, just return empty array or cached data
                const cachedData = this.messageCache.get(chatId);
                if (cachedData) {
                    return cachedData;
                }
                // Return empty array instead of throwing error for cancelled requests
                return [];
            }
            
            console.error(`💥 Error fetching messages for chat ${chatId}:`, {
                error: error,
                errorMessage: error instanceof Error ? error.message : 'Unknown error',
                errorStack: error instanceof Error ? error.stack : undefined,
                chatId: chatId,
                baseUrl: this.baseUrl,
                fullUrl: `${this.baseUrl}/chats/${chatId}/messages`,
                timestamp: new Date().toISOString()
            });
            
            // Check if it's a network error
            if (error instanceof TypeError && error.message.includes('fetch')) {
                console.error('🌐 Network Error: Cannot connect to the API server');
                throw new Error(`Network error: Cannot connect to ${this.baseUrl}. Please check if the server is running.`);
            }
            
            throw new Error(`Error fetching messages: ${error instanceof Error ? error.message : 'Unknown error'}`);
        } finally {
            // Remove the abort controller after the request is complete
            this.removeAbortController(requestKey);
        }
    }

    /**
     * Debug method to test message fetching for a specific chat
     * Only available in development mode
     */
    async debugFetchMessages(chatId: number): Promise<{ success: boolean; data?: any; error?: string }> {
        if (process.env.NODE_ENV !== 'development') {
            return { success: false, error: 'Debug method only available in development' };
        }
        
        try {
            const url = `${this.baseUrl}/chats/${chatId}/messages`;
            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json'
                }
            });
            
            if (!response.ok) {
                const errorText = await response.text();
                return {
                    success: false,
                    error: `HTTP ${response.status}: ${errorText}`
                };
            }
            
            const data = await response.json();
            
            return {
                success: true,
                data: data
            };
        } catch (error) {
            return {
                success: false,
                error: error instanceof Error ? error.message : 'Unknown error'
            };
        }
    }

    /**
     * Create a new chat with cache invalidation
     */
    async createChat({ userId, title }: CreateChatRequest): Promise<Chat> {
        try {
            const response = await fetch(`${this.baseUrl}/chats`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ 
                    user_id: userId, 
                    title 
                })
            });

            if (!response.ok) {
                throw new Error(`Failed to create chat: ${response.statusText}`);
            }

            const newChat = await response.json();
            
            // Invalidate user's chat cache since we added a new chat
            this.clearUserCache(userId);
            
            return newChat;
        } catch (error) {
            console.error('Error creating chat:', error);
            throw new Error('Error creating chat');
        }
    }

    /**
     * Add a message to a chat with cache invalidation and optional response time
     */
    async addMessage({ chatId, sender, text, exchange_id, ai_agent_response_time }: AddMessageRequest): Promise<Message> {
        try {
            const payload: any = { 
                sender, 
                text, 
                exchange_id 
            };

            // Include response time for bot messages
            if (ai_agent_response_time !== undefined && ai_agent_response_time !== null) {
                payload.ai_agent_response_time = ai_agent_response_time;
                // Only log in development mode
                if (process.env.NODE_ENV === 'development') {
                    console.log(`💾 Storing bot message with response time: ${ai_agent_response_time}ms`);
                }
            }

            const response = await fetch(`${this.baseUrl}/chats/${chatId}/messages`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload)
            });

            if (!response.ok) {
                throw new Error(`Failed to add message: ${response.statusText}`);
            }

            const newMessage = await response.json();
            
            // Invalidate chat's message cache since we added a new message
            this.clearChatCache(chatId);
            
            return newMessage;
        } catch (error) {
            console.error('Error adding message:', error);
            throw new Error('Error saving message');
        }
    }

    /**
     * Send message to AI API and get response with timing
     */
    async sendMessageToAPI({ message, exchange_id }: SendMessageRequest): Promise<ApiResponse> {
        const startTime = performance.now(); // Start timing
        
        try {
            const payload: any = {
                messages: [{ role: "user", content: message }],
                agent_id: this.agentId,
                databases: this.databases
            };

            if (exchange_id) {
                payload.exchange_id = exchange_id;
            }

            // Only log detailed debugging info in development mode
            if (process.env.NODE_ENV === 'development') {
                // console.log('📤 Sending message to API with databases:', {
                //     message: message.substring(0, 50) + '...',
                //     databases: this.databases,
                //     agent_id: this.agentId,
                //     exchange_id: exchange_id || 'none',
                //     startTime: new Date().toISOString()
                // });
            }

            const response = await fetch(this.interactUrl, {
                method: 'POST',
                headers: {
                    'accept': 'application/json',
                    'X-API-Key': this.apiKey,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload)
            });

            const endTime = performance.now(); // End timing
            const responseTime = Math.round(endTime - startTime); // Calculate response time in milliseconds

            if (!response.ok) {
                throw new Error(`AI API error: ${response.statusText}`);
            }

            const result = await response.json();
            
            // Add response time to the result
            result.response_time = responseTime;
            
            console.log(`⏱️ AI API Response completed in ${responseTime}ms (${(responseTime/1000).toFixed(2)}s)`);
            
            return result;
        } catch (error) {
            const endTime = performance.now();
            const responseTime = Math.round(endTime - startTime);
            
            console.error(`❌ AI API error after ${responseTime}ms:`, error);
            throw new Error('API error');
        }
    }

    /**
     * Send message to AI API with full conversation history for better context and references
     * This enables the AI to understand "give me different format" type requests
     */
    async sendMessageToAPIWithHistory({ 
        message, 
        conversationHistory = [], 
        exchange_id 
    }: { 
        message: string; 
        conversationHistory?: Array<{role: string, content: string}>; 
        exchange_id?: string; 
    }): Promise<ApiResponse> {
        const startTime = performance.now(); // Start timing
        
        try {
            console.log('🚀 Sending message with conversation history for better context:', {
                currentMessage: message,
                historyLength: conversationHistory.length,
                exchange_id,
                databases: this.databases,
                startTime: new Date().toISOString()
            });

            // Build complete conversation including current message
            const completeConversation = [
                ...conversationHistory,
                { role: "user", content: message }
            ];

            const payload: any = {
                messages: completeConversation,
                agent_id: this.agentId,
                databases: this.databases
            };

            // Include exchange_id if available
            if (exchange_id) {
                payload.exchange_id = exchange_id;
            }

            console.log('📦 Sending payload with conversation context and databases:', {
                messageCount: completeConversation.length,
                firstMessage: completeConversation[0]?.content?.substring(0, 50) + '...',
                currentMessage: message.substring(0, 50) + '...',
                databases: this.databases,
                fullPayload: payload
            });

            const response = await fetch(this.interactUrl, {
                method: 'POST',
                headers: {
                    'accept': 'application/json',
                    'X-API-Key': this.apiKey,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload)
            });

            const endTime = performance.now(); // End timing
            const responseTime = Math.round(endTime - startTime); // Calculate response time in milliseconds

            if (!response.ok) {
                const errorText = await response.text();
                console.error('❌ History API error:', {
                    status: response.status,
                    statusText: response.statusText,
                    errorText,
                    responseTime: `${responseTime}ms`
                });
                throw new Error(`AI API error: ${response.statusText} - ${errorText}`);
            }

            const result = await response.json();
            
            // Add response time to the result
            result.response_time = responseTime;
            
            console.log(`✅ Got response with conversation context in ${responseTime}ms (${(responseTime/1000).toFixed(2)}s)`);
            return result;

        } catch (error) {
            const endTime = performance.now();
            const responseTime = Math.round(endTime - startTime);
            
            console.error(`💥 Error in sendMessageToAPIWithHistory after ${responseTime}ms:`, error);
            throw new Error(`History API error: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
    }

    /**
     * Delete a chat (if needed in future)
     */
    async deleteChat(chatId: number): Promise<void> {
        try {
            const response = await fetch(`${this.baseUrl}/chats/${chatId}`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) {
                throw new Error(`Failed to delete chat: ${response.statusText}`);
            }
        } catch (error) {
            console.error('Error deleting chat:', error);
            throw new Error('Error deleting chat');
        }
    }

    /**
     * Update chat title (if needed in future)
     */
    async updateChatTitle(chatId: number, title: string): Promise<Chat> {
        try {
            const response = await fetch(`${this.baseUrl}/chats/${chatId}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ title })
            });

            if (!response.ok) {
                throw new Error(`Failed to update chat: ${response.statusText}`);
            }

            return await response.json();
        } catch (error) {
            console.error('Error updating chat title:', error);
            throw new Error('Error updating chat title');
        }
    }

    /**
     * Send feedback for a message (thumbs up/down)
     */
    async sendMessageFeedback({ messageId, feedbackFlag }: FeedbackRequest): Promise<void> {
        try {
            const response = await fetch(`${this.baseUrl}/messages/${messageId}/feedback`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ 
                    feedback_flag: feedbackFlag 
                })
            });

            if (!response.ok) {
                throw new Error(`Failed to send feedback: ${response.statusText}`);
            }

            // Clear all message caches since we don't know which chat this message belongs to
            // This ensures that when messages are fetched again, they include the updated feedback
            this.messageCache.clear();
            
            // Clear cache timestamps for all message caches
            for (const key of this.cacheTimestamps.keys()) {
                if (key.startsWith('messages_')) {
                    this.cacheTimestamps.delete(key);
                }
            }

            console.log(`✅ Feedback sent for message ${messageId}: ${feedbackFlag === 1 ? '👍' : '👎'} - Message cache cleared for fresh data`);
        } catch (error) {
            console.error('Error sending feedback:', error);
            throw new Error('Error sending message feedback');
        }
    }

    /**
     * Send detailed feedback message for a specific message
     */
    async sendDetailedFeedback({ messageId, feedbackMessage }: FeedbackMessageRequest): Promise<void> {
        try {
            const response = await fetch(`${this.baseUrl}/messages/${messageId}/feedback/message`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ 
                    feedback_message: feedbackMessage 
                })
            });

            if (!response.ok) {
                throw new Error(`Failed to send detailed feedback: ${response.statusText}`);
            }

            console.log(`✅ Detailed feedback sent for message ${messageId}: "${feedbackMessage.substring(0, 50)}..."`);
        } catch (error) {
            console.error('Error sending detailed feedback:', error);
            throw new Error('Error sending detailed feedback message');
        }
    }

    /**
     * Search chats by title
     */
    async searchChats(userId: number, searchTerm: string): Promise<Chat[]> {
        try {
            const allChats = await this.fetchChats(userId);
            return allChats.filter(chat => 
                chat.title.toLowerCase().includes(searchTerm.toLowerCase())
            );
        } catch (error) {
            console.error('Error searching chats:', error);
            throw new Error('Error searching chats');
        }
    }

    /**
     * Get chat statistics
     */
    async getChatStats(userId: number): Promise<{ totalChats: number; totalMessages: number }> {
        try {
            const chats = await this.fetchChats(userId);
            let totalMessages = 0;
            
            for (const chat of chats) {
                const messages = await this.fetchMessages(chat.id);
                totalMessages += messages.length;
            }
            
            return {
                totalChats: chats.length,
                totalMessages
            };
        } catch (error) {
            console.error('Error getting chat stats:', error);
            throw new Error('Error getting chat statistics');
        }
    }

    /**
     * Clear all user chats (use with caution)
     */
    async clearAllChats(userId: number): Promise<void> {
        try {
            const chats = await this.fetchChats(userId);
            
            // Delete all chats
            await Promise.all(
                chats.map(chat => this.deleteChat(chat.id))
            );
        } catch (error) {
            console.error('Error clearing all chats:', error);
            throw new Error('Error clearing all chats');
        }
    }

    /**
     * Validate if user data is sufficient for API calls
     */
    static validateUserData(userData: any): boolean {
        return !!(userData?.Id && typeof userData.Id === 'number' && userData.Id > 0);
    }

    /**
     * Wait for user data to become available with timeout
     */
    static async waitForUserData(
        getUserData: () => any, 
        timeout: number = 10000
    ): Promise<any> {
        const startTime = Date.now();
        
        while (Date.now() - startTime < timeout) {
            const userData = getUserData();
            if (this.validateUserData(userData)) {
                return userData;
            }
            
            // Wait 100ms before checking again
            await new Promise(resolve => setTimeout(resolve, 100));
        }
        
        throw new Error('User data not available within timeout period');
    }

    /**
     * Get current country configuration
     */
    getCountryInfo() {
        return getCountryInfo();
    }

    /**
     * Get current chatbot configuration
     */
    getConfig() {
        return {
            baseUrl: this.baseUrl,
            agentId: this.agentId,
            databases: this.databases,
            interactUrl: this.interactUrl,
            country: getCountryInfo()
        };
    }

    /**
     * Get response time statistics for a chat
     */
    async getResponseTimeStats(chatId: number): Promise<{
        averageResponseTime: number;
        minResponseTime: number;
        maxResponseTime: number;
        totalBotMessages: number;
        messagesWithTiming: number;
    }> {
        try {
            const messages = await this.fetchMessages(chatId);
            const botMessagesWithTiming = messages.filter(msg => 
                msg.sender === 'bot' && 
                msg.ai_agent_response_time && 
                msg.ai_agent_response_time > 0
            );

            if (botMessagesWithTiming.length === 0) {
                return {
                    averageResponseTime: 0,
                    minResponseTime: 0,
                    maxResponseTime: 0,
                    totalBotMessages: messages.filter(m => m.sender === 'bot').length,
                    messagesWithTiming: 0
                };
            }

            const responseTimes = botMessagesWithTiming.map(msg => msg.ai_agent_response_time!);
            const totalResponseTime = responseTimes.reduce((sum, time) => sum + time, 0);
            const averageResponseTime = Math.round(totalResponseTime / responseTimes.length);
            const minResponseTime = Math.min(...responseTimes);
            const maxResponseTime = Math.max(...responseTimes);

            // Only log response time stats in development mode
            if (process.env.NODE_ENV === 'development') {
                console.log(`📊 Response time stats for chat ${chatId}:`, {
                    average: `${averageResponseTime}ms (${(averageResponseTime/1000).toFixed(2)}s)`,
                    min: `${minResponseTime}ms`,
                    max: `${maxResponseTime}ms`,
                    totalBotMessages: messages.filter(m => m.sender === 'bot').length,
                    messagesWithTiming: botMessagesWithTiming.length
                });
            }

            return {
                averageResponseTime,
                minResponseTime,
                maxResponseTime,
                totalBotMessages: messages.filter(m => m.sender === 'bot').length,
                messagesWithTiming: botMessagesWithTiming.length
            };
        } catch (error) {
            console.error('Error getting response time stats:', error);
            throw new Error('Error getting response time statistics');
        }
    }

    /**
     * Test API connection and configuration
     */
    async testConnection(): Promise<{ success: boolean; message: string; details?: any }> {
        try {
           // console.log('🔍 Testing API connection...');
            console.log('📍 Base URL:', this.baseUrl);
            
            // Test a simple endpoint first
            const testUrl = `${this.baseUrl}/health`;
            console.log('🏥 Testing health endpoint:', testUrl);
            
            const healthResponse = await fetch(testUrl, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json'
                }
            });
            
            console.log('🏥 Health check response:', {
                status: healthResponse.status,
                statusText: healthResponse.statusText,
                ok: healthResponse.ok
            });
            
            if (healthResponse.ok) {
                return {
                    success: true,
                    message: 'API connection successful',
                    details: {
                        baseUrl: this.baseUrl,
                        status: healthResponse.status
                    }
                };
            } else {
                return {
                    success: false,
                    message: `API health check failed: ${healthResponse.status} ${healthResponse.statusText}`,
                    details: {
                        baseUrl: this.baseUrl,
                        status: healthResponse.status,
                        statusText: healthResponse.statusText
                    }
                };
            }
        } catch (error) {
            console.error('💥 Connection test failed:', error);
            return {
                success: false,
                message: `Connection test failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
                details: {
                    baseUrl: this.baseUrl,
                    error: error instanceof Error ? error.message : 'Unknown error'
                }
            };
        }
    }

    /**
     * Cancel specific ongoing requests
     */
    cancelRequest(requestType: 'fetchMessages' | 'fetchChats', id?: number): void {
        if (id) {
            const key = `${requestType}_${id}`;
            const controller = this.abortControllers.get(key);
            if (controller && !controller.signal.aborted) {
                controller.abort();
                this.removeAbortController(key);
            }
        } else {
            // Cancel all requests of this type
            const keysToCancel = Array.from(this.abortControllers.keys()).filter(key => key.startsWith(requestType));
            keysToCancel.forEach(key => {
                const controller = this.abortControllers.get(key);
                if (controller && !controller.signal.aborted) {
                    controller.abort();
                    this.removeAbortController(key);
                }
            });
        }
    }

    /**
     * Cancel all ongoing requests
     */
    cancelAllRequests(): void {
        this.abortControllers.forEach((controller, key) => {
            if (!controller.signal.aborted) {
                controller.abort();
            }
        });
        this.abortControllers.clear();
    }

}

// Export singleton instance
export const chatbotService = new ChatbotService();

// Export the class for testing if needed
export default ChatbotService;
